/* AddLicenseComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;

const Renderer = require("./AddLicenseRenderer");

/**
 * @class AddLicenseComponent
 * @extends EventEmitter
 *
 * @fires AddLicenseComponent#Data
 * @fires AddLicenseComponent#Error
 * @fires AddLicenseComponent#Shutdown
 */
class AddLicenseComponent extends EventEmitter {
    constructor({ validate, addLicense }) {
        super();
        this._renderer = new Renderer(this);
        this._validate = validate;
        this._addLIcense = addLicense;

        this._data = null;
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer && this._renderer.getRootElement();
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    _shutdown() {
        this._triggerShutdown();
        this._unregisterListeners();
        this.removeAllListeners();
        return Promise.all([this._renderer.shutdown()])
            .then(() => {
                this._renderer = null;
                this._shutdownProm = null;
                this._listeners = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    closePopUp() {
        this.rejectPopUp({ userRejected: true });
    }

    resolvePopup(data) {
        this._context.resolve(data);
    }

    rejectPopUp(data) {
        this._context.reject(data);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    _triggerShutdown() {
        /**
         * @event AddLicenseComponent#Shutdown
         */
        this.emit("Shutdown");
    }

    _triggerData(data) {
        /**
         * @event AddLicenseComponent#Data
         * @param {Object[]} data - new data
         */
        this.emit("Data", data);
    }
    _triggerError(errorMsg) {
        /**
         * @event AddLicenseComponent#Error
         * @param {string} errorMsg - error message
         */
        this.emit("Error", errorMsg);
    }

    _setData(data, silent) {
        this._data = data;
        if (!silent) {
            this._triggerData(data);
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    getData() {
        return this._data;
    }

    validate(key) {
        this._setData(null, true);
        this._validate(key).then((licenseData) => {
            let data;
            if (licenseData.errorMsg) {
                this._triggerError(licenseData.errorMsg);
                data = null;
            } else {
                data = {
                    key: key,
                    license: this.parseLicense(licenseData)
                };
            }
            this._setData(data);
        });
    }

    activate() {
        if (this._data && this._data.key) {
            return this._addLIcense(this._data.key).then((licenseData) => {
                if (licenseData.errorMsg) {
                    this._triggerError(licenseData.errorMsg);
                } else {
                    this.closePopUp();
                }
            });
        } else {
            return Promise.reject("No Data");
        }
    }

    parseLicense(licenseData) {
        const licenses = [].concat(licenseData || []);
        const parseSolution = ({ licenseKey, contractId, solutionLicenses }) =>
            Object.entries(solutionLicenses || {}).map(([solutionKey, solutionInfo]) =>
                Object.assign(solutionInfo, {
                    id: `${licenseKey}_${solutionKey}`,
                    licenseKey,
                    solutionKey,
                    contractId
                })
            );
        return licenses.reduce((data, license) => data.concat(parseSolution(license)), []);
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = AddLicenseComponent;
